import { useState } from 'react';
import { Alert, Modal, Button, Form, message } from 'antd';
import { useCreateCobro } from '../api/useCobros';
import { useT } from '../../../locales';
import { CobroForm } from './CobroForm';
import { buildRecreationOperationKey, readCobroCreateFailure } from '../lib/cobro-create-error';

interface CobroCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  preselectClienteId?: number;
}

interface PendingRecreation {
  values: Record<string, unknown>;
  /** Backend message shown verbatim in the confirmation dialog. */
  message: string | null;
  annulledInvoiceId: number | null;
}

export function CobroCreateModal({
  open,
  onClose,
  onSuccess,
  preselectClienteId,
}: CobroCreateModalProps) {
  const t = useT();
  const [form] = Form.useForm();
  const createCobro = useCreateCobro();
  const [pendingRecreation, setPendingRecreation] = useState<PendingRecreation | null>(null);

  const closeAfterSuccess = () => {
    setPendingRecreation(null);
    message.success(t('pages.cobros.createSuccess'));
    onClose();
    onSuccess?.();
  };

  const submit = (values: Record<string, unknown>, operacionKey?: string) => {
    const dto = { ...(values as Parameters<typeof createCobro.mutateAsync>[0]) };
    if (operacionKey !== undefined) dto.operacionKey = operacionKey;
    return createCobro.mutateAsync(dto);
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    try {
      await submit(values);
      closeAfterSuccess();
    } catch (error) {
      const failure = readCobroCreateFailure(error);
      if (failure.isAnnulledInvoiceConflict) {
        // Explicit operator confirmation: never silently retry with a new key.
        setPendingRecreation({
          values,
          message: failure.message,
          annulledInvoiceId: failure.annulledInvoiceId,
        });
        return;
      }
      message.error(failure.message ?? t('pages.cobros.createError'));
    }
  };

  const handleConfirmRecreation = async () => {
    if (pendingRecreation === null) return;
    const operacionKey = buildRecreationOperationKey(
      pendingRecreation.values,
      pendingRecreation.annulledInvoiceId,
    );
    try {
      await submit(pendingRecreation.values, operacionKey);
      closeAfterSuccess();
    } catch (error) {
      const failure = readCobroCreateFailure(error);
      if (
        failure.isAnnulledInvoiceConflict &&
        failure.annulledInvoiceId !== null &&
        failure.annulledInvoiceId !== pendingRecreation.annulledInvoiceId
      ) {
        // The ledger named the invoice the attempted key actually created —
        // one step newer than the id embedded in that key. Adopt it as the
        // next pending identity and ask the operator to confirm again (the
        // dialog stays open with the backend message verbatim). Never mint a
        // key for an id the backend did not just name, and never resubmit
        // without confirmation.
        setPendingRecreation({
          values: pendingRecreation.values,
          message: failure.message,
          annulledInvoiceId: failure.annulledInvoiceId,
        });
        return;
      }
      setPendingRecreation(null);
      message.error(failure.message ?? t('pages.cobros.createError'));
    }
  };

  const handleClose = () => {
    setPendingRecreation(null);
    onClose();
  };

  return (
    <>
      <Modal
        title={t('pages.cobros.create')}
        open={open}
        onCancel={handleClose}
        destroyOnHidden
        centered
        width={640}
        styles={{
          body: {
            maxHeight: 'min(75vh, 675px)',
            overflowY: 'auto',
          },
        }}
        footer={[
          <Button key="cancel" onClick={handleClose}>
            {t('common.cancel')}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={createCobro.isPending}
            onClick={() => form.submit()}
          >
            {t('pages.cobros.create')}
          </Button>,
        ]}
      >
        <CobroForm
          mode="create"
          form={form}
          preselectedClienteId={preselectClienteId}
          hideSubmit
          onSubmit={handleSubmit}
          loading={createCobro.isPending}
        />
      </Modal>
      <Modal
        title={t('pages.cobros.recrearAnuladaTitle')}
        open={pendingRecreation !== null}
        onCancel={() => setPendingRecreation(null)}
        onOk={() => void handleConfirmRecreation()}
        confirmLoading={createCobro.isPending}
        okText={t('pages.cobros.recrearAnuladaOk')}
        cancelText={t('common.cancel')}
        centered
        width={480}
        destroyOnHidden
      >
        <p>{t('pages.cobros.recrearAnuladaConfirm')}</p>
        {pendingRecreation !== null && pendingRecreation.message !== null ? (
          <Alert type="warning" showIcon message={pendingRecreation.message} />
        ) : null}
      </Modal>
    </>
  );
}
